import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Box, Stack, styled} from '@mui/material';
import {FeatureName} from '@contentlab/types/project';
import generateBestPracticesTooltipContent from '@contentlab/utils/generateBestPracticesTooltipContent';
import CreditEndModal from '@contentlab/views/contentlab/pricing/components/credit-end-modal/CreditEndModal';
import contentLabStore, {contentLabActions} from '../../stores/contentLabStore';
import SplitLayout from '../split-layout/SplitLayout';
import FeatureDetails from './components/feature-details/FeatureDetails';
import FileUploader from './components/file-upload/FileUploader';
import GenerateProject from './components/generate-project/GenerateProject';
import LearnMoreModal from './components/learn-more-modal';
import RenameProject from './components/rename-project/RenameProject';

type FeatureEditorProps = {
  testID?: string;
  feature: FeatureName;
  featureDetailsProps: React.ComponentProps<typeof FeatureDetails>;
  renameProjectProps: React.ComponentProps<typeof RenameProject>;
  generateProjectProps: React.ComponentProps<typeof GenerateProject>;
  fileUploaderProps: Omit<React.ComponentProps<typeof FileUploader>, 'feature'>;
  rightColumnOverride?: React.ReactNode;
  hideGenerateSection?: boolean;
};

const Root = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  width: '100%',
  height: '100%',
  maxWidth: '1600px',
  minWidth: '1053px',
  margin: '0 auto',
}));

function FeatureEditor({
  testID = 'feature-editor',
  feature,
  renameProjectProps,
  featureDetailsProps,
  generateProjectProps,
  fileUploaderProps,
  rightColumnOverride,
  hideGenerateSection,
}: FeatureEditorProps) {
  const isLearnMoreModalOpen = contentLabStore(
    useShallow((state) => state.isLearnMoreModalOpen)
  );

  const leftColumn = (
    <Stack direction="column" gap="24px" width="100%" height="100%">
      <RenameProject {...renameProjectProps} />
      <FileUploader {...fileUploaderProps} feature={feature} showUploadErrors />
    </Stack>
  );

  const rightColumn = rightColumnOverride || (
    <>
      <FeatureDetails {...featureDetailsProps} />
      {hideGenerateSection ? null : (
        <GenerateProject {...generateProjectProps} />
      )}
    </>
  );

  return (
    <Root data-testid={testID}>
      <SplitLayout leftColumn={leftColumn} rightColumn={rightColumn} />
      <LearnMoreModal
        isOpen={isLearnMoreModalOpen}
        onClose={() => contentLabActions.setIsLearnMoreModalOpen(false)}
        {...generateBestPracticesTooltipContent(feature)}
      />
      <CreditEndModal />
    </Root>
  );
}

export default FeatureEditor;
